import React from "react";

import Grid from "components/Grid";
import Panel from "components/Panel";
import { useRoutes } from "utils/routes";
import { makeFilter } from "utils/filter";
import { useQueryStringSearch } from "components/QueryStringSearch";
import { Link } from "react-router-dom";
import { formatNumberWithThreshold, formatTimestampShort } from "utils/format";
import moment from "moment";
import ModalContainer from "components/ModalContainer";

import { GetIndexAdvisorConfigForDatabaseQuery } from "../gql/Query.tables.generated";
type IndexAdvisorConfig =
  GetIndexAdvisorConfigForDatabaseQuery["getIndexAdvisorConfigForDatabase"];

const Details: React.FunctionComponent<{
  databaseId: string;
  data: IndexAdvisorConfig;
  settingName: string;
  onClose: () => void;
}> = ({ databaseId, data, settingName, onClose }) => {
  const title = `Tables using ${settingName} configuration`;
  const tablesData = data.filter((val) => val.settingsName === settingName);

  return (
    <ModalContainer title={title} className="md:ml-[500px]" onClose={onClose}>
      <TableList databaseId={databaseId} tablesData={tablesData} />
    </ModalContainer>
  );
};

const TableList: React.FunctionComponent<{
  databaseId: string;
  tablesData: IndexAdvisorConfig;
}> = ({ databaseId, tablesData }) => {
  const { databaseTableIndexSelection } = useRoutes();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  const filteredData = tablesData.filter(makeFilter(searchTerm, "tableName"));

  return (
    <Panel
      title={`Tables (${filteredData.length})`}
      secondaryTitle={<SearchInput />}
    >
      <Grid
        data={filteredData}
        defaultSortBy="lastRunAt"
        columns={[
          {
            field: "tableName",
            header: "Table",
            width: "1fr",
            renderer: function TableCell({ fieldData, rowData }) {
              return (
                <Link
                  to={databaseTableIndexSelection(
                    databaseId,
                    rowData.id.toString(),
                  )}
                >
                  {fieldData}
                </Link>
              );
            },
          },
          {
            field: "scansPerMin",
            header: "Scans/Min",
            width: "120px",
            renderer: ({ fieldData }) =>
              formatNumberWithThreshold(fieldData, 1),
            style: "number",
          },
          {
            field: "lastRunAt",
            header: "Last Run",
            width: "220px",
            renderer: function LastRunCell({ fieldData }) {
              return formatTimestampShort(moment.unix(fieldData));
            },
            nullValue: "n/a",
            tip: (
              <>
                The last time the Indexing Engine processed this table to find
                insights. Shows n/a if the Indexing Engine run was skipped due
                to the Ignored configuration or if there were no scans on the
                table in the last 7 days. Newly created tables that the Indexing
                Engine hasn't processed yet also show as n/a.
              </>
            ),
            defaultSortOrder: "DESC",
          },
        ]}
        pageSize={10}
      />
    </Panel>
  );
};

export default Details;
